import type { Subanta } from '../api/subantaApi';

/** Scalar-ish slice used for list fingerprint (list endpoint often omits heavy relations). */
function compactListRowForFingerprint(s: Subanta): unknown {
  return {
    id: s.id,
    updatedAt: s.updatedAt ?? s.updated_at,
    word: s.word,
    base_word: s.base_word,
    iast: s.iast,
    bat: s.bat,
    linga: s.linga,
    vyutpanna: s.vyutpanna,
    prakar: s.prakar,
    shreni: s.shreni,
    shatwa: s.shatwa,
    natwa: s.natwa,
    vedic: s.vedic,
    barga: s.barga,
    bachan: s.bachan,
  };
}

export function fingerprintSubantaList(rows: Subanta[]): string {
  return JSON.stringify(
    [...rows].sort((a, b) => a.id - b.id).map(compactListRowForFingerprint)
  );
}

/** Relation-aware fingerprint for GET /subanta/:id payloads. */
function compactDetailForFingerprint(s: Subanta): unknown {
  return {
    id: s.id,
    updatedAt: s.updatedAt ?? s.updated_at,
    word: s.word,
    base_word: s.base_word,
    iast: s.iast,
    bat: s.bat,
    linga: s.linga,
    vyutpanna: s.vyutpanna,
    prakar: s.prakar,
    shreni: s.shreni,
    shatwa: s.shatwa,
    natwa: s.natwa,
    vedic: s.vedic,
    barga: s.barga,
    bachan: s.bachan,
    arthas: Array.isArray(s.arthas)
      ? [...s.arthas]
          .sort((a, b) => a.id - b.id)
          .map((a) => ({
            id: a.id,
            languageId: a.languageId,
            text: a.text,
            order: a.order ?? null,
            bidha: a.bidha ?? null,
            prakar: a.prakar ?? null,
          }))
      : [],
    rups: Array.isArray(s.rups)
      ? [...s.rups].sort((a, b) => a.id - b.id).map((r) => ({ id: r.id, linga: r.linga }))
      : [],
    samastaWordsLen: Array.isArray(s.samastaWords) ? s.samastaWords.length : 0,
    samastasLen: Array.isArray(s.samastas) ? s.samastas.length : 0,
    vyutpattisLen: Array.isArray(s.vyutpattis) ? s.vyutpattis.length : 0,
    nispattisLen: Array.isArray(s.nispattis) ? s.nispattis.length : 0,
  };
}

export function fingerprintSubantaDetail(s: Subanta): string {
  return JSON.stringify(compactDetailForFingerprint(s));
}

let cachedSubantaList: Subanta[] | null = null;
let cachedListFingerprint: string | null = null;

const detailById = new Map<number, Subanta>();
const detailFingerprintById = new Map<number, string>();

export function getCachedSubantaList(): Subanta[] | null {
  return cachedSubantaList;
}

/**
 * Updates list cache and reports whether the payload differs from the previous snapshot.
 */
export function mergeSubantaListCache(fetched: Subanta[]): { changed: boolean; next: Subanta[] } {
  const fp = fingerprintSubantaList(fetched);
  if (cachedListFingerprint === fp && cachedSubantaList != null) {
    return { changed: false, next: cachedSubantaList };
  }
  cachedListFingerprint = fp;
  cachedSubantaList = fetched;
  return { changed: true, next: fetched };
}

export function getCachedSubantaDetail(id: number): Subanta | undefined {
  return detailById.get(id);
}

export function mergeSubantaDetailCache(
  id: number,
  fetched: Subanta
): { changed: boolean; next: Subanta } {
  const fp = fingerprintSubantaDetail(fetched);
  const prevFp = detailFingerprintById.get(id);
  if (prevFp === fp && detailById.has(id)) {
    return { changed: false, next: detailById.get(id) as Subanta };
  }
  detailFingerprintById.set(id, fp);
  detailById.set(id, fetched);
  return { changed: true, next: fetched };
}

/** Clear caches after creates/updates/deletes so the next fetch is trusted. */
export function invalidateSubantaCaches(): void {
  cachedSubantaList = null;
  cachedListFingerprint = null;
  detailById.clear();
  detailFingerprintById.clear();
}

export function invalidateSubantaDetail(id: number): void {
  detailById.delete(id);
  detailFingerprintById.delete(id);
}
